/**
 * Application Bootstrap
 * 
 * Main application orchestrator that wires together all systems:
 * - Grid computation and allocation
 * - Animation loop
 * - Rendering (canvas + DOM)
 * - Profile management
 * - UI controls
 * - Event handlers
 * 
 * This class is fully state-driven and reacts to changes in core state via EventBus.
 */

// Version injected at build time by Vite
declare const __APP_VERSION__: string;

import { bus } from '../core/events.js';
import { state, setState, updateState } from '../core/state.js';
import { log, trace } from '../services/logging/index.js';
import { computeGridResolution } from '../core/grid.js';
// Removed GridAllocator - using simple allocator instead
import { viewportAllocationIntegration } from '../systems/viewport-allocation-integration.js';
import { viewManager } from '../systems/view-manager.js';
import { processManager } from '../systems/process-manager.js';
import { AnimationLoop } from '../animation/loop.js';
import { CanvasRenderer, createLogoImage } from '../rendering/canvas.js';
import { BackgroundCanvasRenderer } from '../rendering/background-canvas.js';
import { createBackgroundImage, createBackgroundVideo, type BackgroundState, type BackgroundType, type CheckerboardBackground } from '../rendering/background.js';
import {
  initializeMajakuPrivacyBackground,
  activateMajakuPrivacyBackground,
  deactivateMajakuPrivacyBackground,
} from '../services/majaku-privacy-background/index.js';
import {
  startStroke,
  continueStroke,
  endStroke,
  renderAllStrokes,
} from '../services/drawing/index.js';
import { createLogoState, updateLogoState, createLedFlashState, updateLedFlashState, setDismissButtonHover } from '../rendering/effects.js';
import {
  createRippleEffect,
  addRipple,
  updateRipples,
  updateSourceTexture,
  renderRipple,
  type RippleEffectState,
} from '../rendering/shaders/ripple-effect.js';
// REMOVED: ensureDom, updateDOM - BaseView handles its own DOM creation and updates
import {
  createContextMenuHandler,
  createAuxClickHandler,
} from '../ui/handlers.js';
// Actions removed - no longer needed without animation
import type { GridElement } from '../grid-allocation-v2/types.js';
import { updateGutters } from '../utils/viewport.js';
import { AnimationProfileType } from '../types/enums.js';
import { UrlManager } from './url-modes/url-manager.js';
// Page switcher and backstage controls removed - app now uses single horizontal feed
import { drawDragDropZone, isPointInDragDropZone, type DragDropZoneState } from '../rendering/drag-drop-zone.js';
import { getCurrentTheme, type Theme } from '../services/theme/index.js';
import { initializeCaptions, startCaptions, stopCaptions } from '../services/captions/index.js';
import { initializeProactiveAgent } from '../services/notifications/proactive/index.js';
import { initializeAuthPromptListener } from '../services/auth-prompt/index.js';
import { viewAnimationManager } from '../animation/view-animation-manager.js';
import { createHeader } from '../rendering/header.js';

// Grid elements are created by ViewManager from Processes/Views

/**
 * Main application class
 * 
 * Fully state-driven: reads from core state and reacts to EventBus events.
 */
class App {
  private animationLoop: AnimationLoop;
  private renderer: CanvasRenderer;
  private backgroundRenderer: BackgroundCanvasRenderer;
  private bgImageState: ReturnType<typeof createBackgroundImage>;
  private bgVideoStates: ReturnType<typeof createBackgroundVideo>[] = [];
  private bgState: BackgroundState;
  private currentBackgroundIndex: number = 0;
  private backgrounds: Array<{
    type: BackgroundType;
    image?: ReturnType<typeof createBackgroundImage>;
    gradient?: { type: 'gradient'; topColor: string; bottomColor: string };
    checkerboard?: CheckerboardBackground;
    video?: ReturnType<typeof createBackgroundVideo>;
    majakuPrivacy?: boolean;
  }> = [];
  private logoImageState: ReturnType<typeof createLogoImage>;
  private logoAnimState: ReturnType<typeof createLogoState>;
  private ledFlashState: ReturnType<typeof createLedFlashState>;
  private rippleEffectState: RippleEffectState | null;
  private busSubscriptions: Array<{ event: string; handler: (payload?: unknown) => void }> = [];
  private stageElement: HTMLElement | null;
  private layerElement: HTMLElement | null;
  private layerContentElement: HTMLElement | null = null; // Cached reference to .layer-content
  private aiVisualizationElement: HTMLElement | null = null; // Cached reference to .ai-visualization
  private canvasElement: HTMLCanvasElement | null;
  private backgroundCanvasElement: HTMLCanvasElement | null;
  // Cursor state
  private cursorElement: HTMLElement | null = null;
  private cursorCurrentRadius: number = 12; // Current radius (lerped)
  private cursorTargetRadius: number = 12; // Target radius (12px idle, 6px when hovering)
  private cursorLerpSpeed: number = 0.15; // Lerp speed (0-1, higher = faster)
  private isHoveringClickable: boolean = false; // Track if hovering over clickable element
  private cursorBorderWidthIdle: number = 1.5; // Border thickness in pixels when idle
  private cursorBorderWidthActive: number = 2; // Border thickness in pixels when active (hollow)
  private cursorLastTargetRadius: number = 12; // Track last target to detect changes
  private cursorAnimationActive: boolean = false; // Track if cursor is animating
  // Current theme (updated when background changes)
  private currentTheme: Theme | null = null;
  // Grid elements are created by ViewManager from Processes/Views
  private isRecomputing = false; // Guard to prevent recursive recompute calls
  // Removed GridAllocator - using simple allocator in viewportAllocationIntegration
  private urlManager: UrlManager;
  private _justAllocated = false; // Flag to prevent render loop from state.elements -> recompute()
  private _currentRenderFrameId: number = 0; // Track current render frame
  // Page switcher and backstage controls removed - app now uses single horizontal feed
  private dragDropZoneState: DragDropZoneState = {
    isDragging: false,
    isOverZone: false,
    fileCount: 0,
    cardCount: 0
  };

  constructor() {
    // Grid elements come from Processes/Views

    // Get DOM elements
    this.stageElement = document.getElementById('stage');
    this.layerElement = document.getElementById('layer');
    this.canvasElement = document.getElementById('fx') as HTMLCanvasElement | null;
    this.backgroundCanvasElement = document.getElementById('bg') as HTMLCanvasElement | null;
    
    // Setup horizontal scroll container
    this.setupHorizontalScrollContainer();
    
    // Cache layer-content element reference
    this.layerContentElement = this.layerElement?.querySelector('.layer-content') as HTMLElement | null;
    
    // AI visualization removed - metaball overlay handles this functionality now

    if (!this.canvasElement) {
      throw new Error('Main canvas element (#fx) not found');
    }

    if (!this.backgroundCanvasElement) {
      throw new Error('Background canvas element (#bg) not found');
    }

    // Initialize subsystems
    this.animationLoop = new AnimationLoop();
    this.renderer = new CanvasRenderer(this.canvasElement);
    this.backgroundRenderer = new BackgroundCanvasRenderer(this.backgroundCanvasElement);
    
    // Initialize URL manager early so we can check modes during bootstrap
    this.urlManager = new UrlManager();

    // Use FX canvas directly for WebGL rendering (always via shaders)
    this.rippleEffectState = createRippleEffect(this.canvasElement);
    this.bgImageState = createBackgroundImage(
      'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1920&q=80'
    );

    // Initialize video backgrounds from compressed videos
    const videoSources = this.getCompressedVideoSources();
    this.bgVideoStates = videoSources.map(src => createBackgroundVideo(src));

    // Initialize majaku-privacy background (async, will be ready when needed)
    if (this.stageElement) {
      initializeMajakuPrivacyBackground(this.stageElement)
        .then((result) => {
          // Background initialized
        })
        .catch((error) => {
          log.error('Failed to initialize majaku-privacy background', { error });
        });
    } else {
      // Cannot initialize majaku-privacy background - stageElement is null
    }

    // Network visualization is now integrated into majaku-privacy-background
    // No separate initialization needed

    // Drawing service is now initialized by the drawing process (on-demand)
    // No need to initialize here

    // Initialize captions
    if (!this.urlManager.isWorkshopMode()) {
      initializeCaptions();
    }
    
    // Create HTML header
    const header = createHeader();
    if (this.stageElement) {
      this.stageElement.appendChild(header);
    }
    
    // Initialize proactive agent (fetches and displays contextual messages on page load)
    if (!this.urlManager.isWorkshopMode()) {
      initializeProactiveAgent().catch((error) => {
        log.error('Failed to initialize proactive agent', { error });
      });
    }
    
    // Initialize auth prompt listener (handles authentication prompts from API)
    if (!this.urlManager.isWorkshopMode()) {
      initializeAuthPromptListener();
    }

    // Initialize backgrounds array
    this.backgrounds = [
      {
        type: 'image',
        image: this.bgImageState,
      },
      {
        type: 'drawing',
      },
      {
        type: 'gradient',
        gradient: {
          type: 'gradient',
          topColor: '#ffffff',
          bottomColor: '#ff0000',
        },
      },
      {
        type: 'gradient',
        gradient: {
          type: 'gradient',
          topColor: '#F5FAFF',
          bottomColor: '#DCE9F6',
        },
      },
      {
        type: 'checkerboard',
        checkerboard: {
          type: 'checkerboard',
          color1: '#CF0',
          color2: '#ffffff',
          tileSize: 50,
        },
      },
      // Add video backgrounds
      ...this.bgVideoStates.map(video => ({
        type: 'video' as const,
        video: video,
      })),
      // Add majaku-privacy background
      {
        type: 'majaku-privacy' as const,
        majakuPrivacy: true,
      },
    ];

    // Initialize background state - load from saved state or default to majaku-privacy
    // Find majaku-privacy background index (it's the last one in the array)
    const majakuPrivacyIndex = this.backgrounds.findIndex(bg => bg.type === 'majaku-privacy');
    const defaultBackgroundIndex = majakuPrivacyIndex >= 0 ? majakuPrivacyIndex : 0;
    
    const savedBackgroundIndex = state.ui?.backgroundIndex ?? defaultBackgroundIndex;
    
    // Force majaku-privacy background in workshop mode
    const targetIndex = this.urlManager.isWorkshopMode() ? majakuPrivacyIndex : savedBackgroundIndex;
    
    const initialIndex = Math.max(0, Math.min(targetIndex, this.backgrounds.length - 1));
    this.currentBackgroundIndex = initialIndex;
    
    // Initialize theme based on current background
    this.currentTheme = getCurrentTheme(this.currentBackgroundIndex, this.backgrounds);

    const selectedBg = this.backgrounds[this.currentBackgroundIndex];
    this.bgState = {
      type: selectedBg.type,
      image: selectedBg.image,
      gradient: selectedBg.gradient,
      checkerboard: selectedBg.checkerboard,
      video: selectedBg.video,
    };

    // Update state with background type and data to avoid brittle index checking
    updateState('ui', (draft) => {
      if (!draft) draft = {};
      draft.backgroundIndex = this.currentBackgroundIndex;
      draft.backgroundType = selectedBg.type;
      draft.backgroundData = selectedBg.gradient ? { gradient: selectedBg.gradient } : undefined;
    });
    
    // If starting with drawing theme, emit event for drawing process to handle
    if (selectedBg.type === 'drawing') {
      bus.emit('drawing.activate', {});
      // Set pointer-events: none on FX canvas to allow pass-through to background canvas
      if (this.canvasElement) {
        this.canvasElement.style.pointerEvents = 'none';
      }
    }

    // Handle majaku-privacy background activation/deactivation on initial load
    if (selectedBg.type === 'majaku-privacy') {
      // Wait for initialization to complete before activating
      // Use a longer delay and check if initialization is complete
      const tryActivate = (attempts: number = 0) => {
        if (attempts > 50) { // Max 5 seconds (50 * 100ms)
          return;
        }
        
        // Check if initialization is complete by checking the state
        // We'll activate it - the activate function will wait if needed
        activateMajakuPrivacyBackground();
        if (this.backgroundCanvasElement) {
          this.backgroundCanvasElement.style.display = 'none';
        }
      };
      
      // Start trying to activate after a short delay
      setTimeout(() => tryActivate(), 200);
    } else {
      if (this.backgroundCanvasElement) {
        this.backgroundCanvasElement.style.display = 'block';
      }
    }
    // Create logo image based on current theme
    // Grid computation now assumes logo will be present and uses predicted dimensions
    // This eliminates layout shifts by computing correct gutters from the start
    // Pass callback to trigger re-render when logo loads (fixes race condition)
    this.logoImageState = createLogoImage(this.currentTheme.logoImage, () => {
      this.renderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    });
    this.logoAnimState = createLogoState();
    this.ledFlashState = createLedFlashState();

    // Initialize cursor
    this.setupCursor();

    // Set up animation loop
    this.animationLoop.setTickCallback(() => this.tick());

    // URL manager initialized early (see top of constructor)

    // Ensure state has squares and grid (boot should have initialized them)
    this.ensureState();

    // Initialize three-layer allocation system
    // The integration auto-initializes when grid state is available
    if (state.grid && this.layerElement) {
      viewportAllocationIntegration.initialize(state.grid, this.layerElement);
    }

    // Set up EventBus listeners for reactive updates
    this.setupStateListeners();

    // Set up UI handlers
    this.setupHandlers();
    
    // Set up drag-drop handlers
    this.setupDragDropHandlers();
    
    // Initialize API upload detection for metaball shape changes
    import('../services/majaku-privacy-background/api-upload-detector.js').then(({ startUploadDetection }) => {
      startUploadDetection();
    }).catch(() => {
      // Upload detector not available, ignore
    });
    
    // Set up resize handler
    this.setupResizeHandler();

    // CRITICAL: Compute initial grid dimensions before views are registered
    // This ensures the grid has valid dimensions for allocation
    // recompute() will compute grid dimensions based on viewport size
    this.recompute();

    // Initial render from state
    this.applyLayoutFromState().then(() => {
      // Initial render after layout is applied
      this.render();
    });
  }

  /**
   * Ensure state has required properties
   */
  private ensureState(): void {
    if (!state.elements) {
      setState('elements', []); // EMPTY - elements come from Processes/Views only
    }
    if (!state.grid) {
      const gap = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gap')) || 10;
      const gutters = updateGutters();
      setState('grid', {
        cols: 4,
        rows: 3,
        cellW: 200,
        cellH: 200,
        gap,
        gutterX: gutters.gutterX,
        gutterY: gutters.gutterY,
        width: 0,
        height: 0,
      });
    }
    if (!state.layout) {
      setState('layout', {
        profile: null,
        animationProfile: AnimationProfileType.Smooth,
      });
    }
  }

  /**
   * Set up EventBus listeners for state-driven reactivity
   */
  private setupStateListeners(): void {
    // React to elements changes (but not from our own recompute)
    // Captions auto-start on initialization and show/hide based on audio availability

    const stateElementsHandler = () => {
      // PERFORMANCE: Prevent render loop - don't recompute if we're already recomputing
      // or if the recompute was triggered by our own allocation
      if (state.elements && state.grid && !this.isRecomputing) {
        // Check if this update came from our own allocation
        const justAllocated = (window as any).__pivotJustAllocated === true;
        if (justAllocated) {
          (window as any).__pivotJustAllocated = false; // Reset flag
          // Skip recompute - we just allocated, no need to recompute again
          return;
        }
        
        // Only recompute if elements come from ViewManager
        const viewElements = viewManager.getAllElements();
        if (state.elements.length > 0 && state.elements.length === viewElements.length) {
          // Elements match view elements - safe to recompute
          this.recompute();
        }
      }
    };
    bus.on('state.elements', stateElementsHandler);
    this.busSubscriptions.push({ event: 'state.elements', handler: stateElementsHandler });

    // React to render.update events (e.g., hover state changes)
    const renderUpdateHandler = () => {
      // Trigger immediate canvas re-render
      this.renderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    };
    bus.on('render.update', renderUpdateHandler);
    this.busSubscriptions.push({ event: 'render.update', handler: renderUpdateHandler });

    // Page change handlers removed - app now uses single horizontal feed

    // React to background index changes (e.g., from state restoration)
    const stateUiHandler = () => {
      if (state.ui?.backgroundIndex !== undefined &&
        state.ui.backgroundIndex !== this.currentBackgroundIndex &&
        this.backgrounds.length > 0) {
        const savedIndex = Math.max(0, Math.min(state.ui.backgroundIndex, this.backgrounds.length - 1));
        this.currentBackgroundIndex = savedIndex;

        const selectedBg = this.backgrounds[this.currentBackgroundIndex];
        this.bgState = {
          type: selectedBg.type,
          image: selectedBg.image,
          gradient: selectedBg.gradient,
          checkerboard: selectedBg.checkerboard,
          video: selectedBg.video,
        };

        // Update state with background type and data to avoid brittle index checking
        updateState('ui', (draft) => {
          if (!draft) draft = {};
          draft.backgroundIndex = this.currentBackgroundIndex;
          draft.backgroundType = selectedBg.type;
          draft.backgroundData = selectedBg.gradient ? { gradient: selectedBg.gradient } : undefined;
        });

        // Handle majaku-privacy background activation/deactivation
        if (selectedBg.type === 'majaku-privacy') {
          activateMajakuPrivacyBackground();
          // Hide the regular background canvas when majaku-privacy is active
          if (this.backgroundCanvasElement) {
            this.backgroundCanvasElement.style.display = 'none';
          }
        } else {
          deactivateMajakuPrivacyBackground();
          // Show the regular background canvas for other backgrounds
          if (this.backgroundCanvasElement) {
            this.backgroundCanvasElement.style.display = 'block';
          }
        }

        // Handle drawing background activation/deactivation
        // Emit events for drawing process to handle
        if (selectedBg.type === 'drawing') {
          bus.emit('drawing.activate', {});
          // Set pointer-events: none on FX canvas to allow pass-through to background canvas
          if (this.canvasElement) {
            this.canvasElement.style.pointerEvents = 'none';
          }
        } else if (this.bgState?.type === 'drawing') {
          // Deactivate drawing mode when switching away from drawing
          bus.emit('drawing.deactivate', {});
          // Restore pointer-events on FX canvas
          if (this.canvasElement) {
            this.canvasElement.style.pointerEvents = 'auto';
          }
        }
        
        // Emit background changed event for drawing process
        bus.emit('background.changed', { type: selectedBg.type });

        this.backgroundRenderer.markDirty();
        this.render();
        this.animationLoop.markRedraw();
        this.animationLoop.start();
      }
    };
    bus.on('state.ui', stateUiHandler);
    this.busSubscriptions.push({ event: 'state.ui', handler: stateUiHandler });

    // Reserved for future features

    // CRITICAL: Removed state.grid listener - it was causing infinite loops
    // Grid changes are now handled internally by recompute()
    // External grid mutations (e.g., from resize) must call recompute() explicitly
  }

  /**
   * Set up simple hollow circle cursor with color inversion
   */
  private setupCursor(): void {
    // Create cursor element
    this.cursorElement = document.createElement('div');
    this.cursorElement.style.position = 'fixed';
    this.cursorElement.style.pointerEvents = 'none';
    this.cursorElement.style.zIndex = '99999'; // Highest z-index to ensure cursor is always on top (above debugger modal at 10001)
    this.cursorElement.style.width = '24px';
    this.cursorElement.style.height = '24px';
    this.cursorElement.style.transform = 'translate(-50%, -50%)';
    this.cursorElement.style.border = `${this.cursorBorderWidthIdle}px solid white`;
    this.cursorElement.style.borderRadius = '50%';
    this.cursorElement.style.backgroundColor = 'transparent';
    this.cursorElement.style.display = 'none';
    this.cursorElement.style.transition = 'none'; // No CSS transitions - we handle animation in JS
    // Color inversion using mix-blend-mode
    this.cursorElement.style.mixBlendMode = 'difference';
    // Rotate hue after inversion (30 degrees)
    this.cursorElement.style.filter = 'hue-rotate(30deg)';
    
    document.body.appendChild(this.cursorElement);

    // Track mouse position globally (on document) so cursor appears everywhere
    // This ensures the cursor is visible immediately, not just after the loading screen disappears
    // and works over all elements including the debugger
    document.addEventListener('mousemove', (e) => {
      if (this.cursorElement) {
        this.cursorElement.style.display = 'block';
        this.cursorElement.style.left = `${e.clientX}px`;
        this.cursorElement.style.top = `${e.clientY}px`;
      }
      // Update hover state globally - check if mouse is over clickable elements anywhere
      this.updateCursorHoverState(e.clientX, e.clientY);
    });

    // Track mouse position on stage element for hover state updates (for stage-specific elements)
    if (this.stageElement) {
      this.stageElement.addEventListener('mousemove', (e) => {
        this.updateCursorHoverState(e.clientX, e.clientY);
      });

      this.stageElement.addEventListener('mouseleave', () => {
        // Reset hover state when leaving stage
        // The global mousemove handler will update it correctly if mouse is over clickable elements
        this.isHoveringClickable = false;
        this.cursorTargetRadius = 12; // Return to normal size
      });
    }
  }

  /**
   * Check if an element or its ancestors are clickable
   */
  private isClickableElement(element: Element | null): boolean {
    if (!element) return false;
    
    // Check if element itself is clickable
    const tagName = element.tagName.toLowerCase();
    if (['a', 'button', 'input', 'select', 'textarea'].includes(tagName)) {
      return true;
    }
    
    // Check if element has cursor: pointer style
    const style = window.getComputedStyle(element);
    if (style.cursor === 'pointer') {
      return true;
    }
    
    // Check if element has click handler (has onclick or event listeners)
    if ((element as HTMLElement).onclick) {
      return true;
    }
    
    // Recursively check parent
    return this.isClickableElement(element.parentElement);
  }

  /**
   * Update cursor hover state by checking if mouse is over clickable elements
   */
  private updateCursorHoverState(clientX: number, clientY: number): void {
    let hovering = false;

    // Check page switcher
    if (state.grid) {
      const coords = this.getCanvasCoordinates(clientX, clientY);
      if (coords) {
        // Page switcher and backstage controls hover detection removed
      }
    }

    // Check BaseView hover state (check if any view has a hovered region)
    const activeViews = viewManager.getActiveViews();
    for (const view of activeViews) {
      const baseViewInstance = (view.data as any)?._baseViewInstance;
      if (baseViewInstance && typeof baseViewInstance.hasHoveredRegion === 'function') {
        if (baseViewInstance.hasHoveredRegion()) {
          hovering = true;
          break;
        }
      }
    }

    // Check Cortex network hover (if majaku-privacy background is active)
    if (this.bgState?.type === 'majaku-privacy') {
      const sceneSetupResult = (window as any).__majakuSceneSetupResult;
      const cortexNetwork = sceneSetupResult?.cortexNetwork;
      if (cortexNetwork && cortexNetwork.hoveredNode !== null) {
        hovering = true;
      }
    }

    // Check Metaball hover (if majaku-privacy background is active)
    // Note: Metaball doesn't have explicit hover state, but we can check if mouse is near center
    // For now, we'll skip metaball hover detection as it requires 3D raycaster

    // Check for clickable DOM elements globally (including debugger, buttons, links, etc.)
    if (!hovering) {
      const elementAtPoint = document.elementFromPoint(clientX, clientY);
      if (elementAtPoint && this.isClickableElement(elementAtPoint)) {
        hovering = true;
      }
    }

    // Update hover state
    if (hovering !== this.isHoveringClickable) {
      this.isHoveringClickable = hovering;
      this.cursorTargetRadius = hovering ? 6 : 12; // 6px radius (12px diameter) when hovering, 12px radius (24px diameter) when idle
      // Start animation loop to animate cursor transition
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    }
  }

  /**
   * Set up canvas interaction handlers
   * 
   * NOTE: Attached to stageElement (not canvasElement) because stageElement
   * is the container that actually receives mouse events. The canvas might be
   * behind other elements or have pointer-events disabled.
   */
  private setupCanvasInteractions(): void {
    if (!this.stageElement || !this.canvasElement) return;

    // Get canvas element rect for coordinate conversion
    // Note: Coordinates are in logical pixels (not device pixels)
    const getCanvasCoordinates = (clientX: number, clientY: number): { x: number; y: number } | null => {
      if (!this.canvasElement || !state.grid) return null;
      const rect = this.canvasElement.getBoundingClientRect();
      // Get logical coordinates (same as view geometry coordinates)
      const x = ((clientX - rect.left) / rect.width) * state.grid.width;
      const y = ((clientY - rect.top) / rect.height) * state.grid.height;
      return { x, y };
    };

      // Handle mouse move for hover detection and drawing
      // Attach to stageElement (the working one) instead of canvasElement
      // NOTE: This will coexist with the shader effect handler in setupHandlers()
      this.stageElement.addEventListener('mousemove', (e) => {
        const coords = getCanvasCoordinates(e.clientX, e.clientY);
        if (!coords) {
          return;
        }

        // In drawing mode: continue stroke if drawing (page controls removed)
        if (this.bgState?.type === 'drawing') {
          // Page switcher and backstage controls hover detection removed
          
          // Update cursor hover state
          this.updateCursorHoverState(e.clientX, e.clientY);
          
          // Continue drawing stroke if mouse is down (left or right button)
          if ((e.buttons & 1) === 1 || (e.buttons & 2) === 2) {
            if (this.backgroundCanvasElement && state.grid) {
              const bgRect = this.backgroundCanvasElement.getBoundingClientRect();
              // Use logical coordinates (not device pixel coordinates)
              // The canvas context is scaled by DPR, so we need logical coordinates
              // Use canvas style dimensions (logical size) not canvas buffer dimensions (device pixels)
              const logicalWidth = parseFloat(this.backgroundCanvasElement.style.width) || state.grid.width || bgRect.width;
              const logicalHeight = parseFloat(this.backgroundCanvasElement.style.height) || state.grid.height || bgRect.height;
              
              // Validate coordinates are valid numbers
              if (isNaN(logicalWidth) || isNaN(logicalHeight) || logicalWidth <= 0 || logicalHeight <= 0) {
                return; // Skip if dimensions are invalid
              }
              
              const bgX = ((e.clientX - bgRect.left) / bgRect.width) * logicalWidth;
              const bgY = ((e.clientY - bgRect.top) / bgRect.height) * logicalHeight;
              
              // Validate coordinates are valid numbers
              if (isNaN(bgX) || isNaN(bgY)) {
                return; // Skip if coordinates are invalid
              }
              
              // Get pressure from pointer event (if available)
              const pressure = (e as PointerEvent).pressure || 0.5;
              const isRightClick = (e.buttons & 2) === 2;
              continueStroke(bgX, bgY, pressure, isRightClick);
            }
          }
          return;
        }

        // Page switcher and backstage controls hover detection removed - app now uses single horizontal feed

        // Update cursor hover state
        this.updateCursorHoverState(e.clientX, e.clientY);

        // Delegate to all active views
        const activeViews = viewManager.getActiveViews();
        for (const view of activeViews) {
          const baseViewInstance = (view.data as any)?._baseViewInstance;
          if (baseViewInstance && typeof baseViewInstance.handleMouseMove === 'function') {
            baseViewInstance.handleMouseMove(coords.x, coords.y);
          }
        }
      });

    // Handle mouse click
    // Attach to stageElement (the working one) instead of canvasElement
    this.stageElement.addEventListener('click', (e) => {
      const coords = getCanvasCoordinates(e.clientX, e.clientY);
      if (!coords) {
        return;
      }

      // Page switcher and backstage controls removed - app now uses single horizontal feed

      // Delegate to all active views
      const activeViews = viewManager.getActiveViews();
      for (const view of activeViews) {
        const baseViewInstance = (view.data as any)?._baseViewInstance;
        if (baseViewInstance && typeof baseViewInstance.handleMouseClick === 'function') {
          baseViewInstance.handleMouseClick(coords.x, coords.y);
        }
      }
    });

    // Handle mouse down (for push-to-talk support and drawing)
    this.stageElement.addEventListener('mousedown', (e) => {
      // Allow right click for drawing mode (rainbow colors)
      // Skip right click only if not in drawing mode
      if (e.button === 2 && this.bgState?.type !== 'drawing') return;
      
      const coords = getCanvasCoordinates(e.clientX, e.clientY);
      if (!coords) return;
      
      // In drawing mode: pass through to drawing (page controls removed)
      if (this.bgState?.type === 'drawing') {
        
        // Pass through to drawing: convert to background canvas coordinates
        if (this.backgroundCanvasElement && state.grid) {
          const bgRect = this.backgroundCanvasElement.getBoundingClientRect();
          // Use logical coordinates (not device pixel coordinates)
          // The canvas context is scaled by DPR, so we need logical coordinates
          // Use canvas style dimensions (logical size) not canvas buffer dimensions (device pixels)
          const logicalWidth = parseFloat(this.backgroundCanvasElement.style.width) || state.grid.width || bgRect.width;
          const logicalHeight = parseFloat(this.backgroundCanvasElement.style.height) || state.grid.height || bgRect.height;
          
          // Validate coordinates are valid numbers
          if (isNaN(logicalWidth) || isNaN(logicalHeight) || logicalWidth <= 0 || logicalHeight <= 0) {
            return; // Skip if dimensions are invalid
          }
          
          const bgX = ((e.clientX - bgRect.left) / bgRect.width) * logicalWidth;
          const bgY = ((e.clientY - bgRect.top) / bgRect.height) * logicalHeight;
          
          // Validate coordinates are valid numbers
          if (isNaN(bgX) || isNaN(bgY)) {
            return; // Skip if coordinates are invalid
          }
          
          // Get pressure from pointer event (if available)
          const pressure = (e as PointerEvent).pressure || 0.5;
          const isRightClick = e.button === 2 || (e.buttons & 2) === 2;
          startStroke(bgX, bgY, pressure, isRightClick);
        }
        return;
      }
      
      // Normal mode: delegate to all active views
      const activeViews = viewManager.getActiveViews();
      for (const view of activeViews) {
        const baseViewInstance = (view.data as any)?._baseViewInstance;
        if (baseViewInstance && typeof baseViewInstance.handleMouseDown === 'function') {
          baseViewInstance.handleMouseDown(coords.x, coords.y);
        }
      }
    });

    // Handle mouse up (for push-to-talk support and drawing)
    // Use document to catch mouseup even if released outside the view
    document.addEventListener('mouseup', (e) => {
      // In drawing mode: end stroke (allow both left and right click)
      if (this.bgState?.type === 'drawing') {
        endStroke();
        return;
      }
      
      // Skip if right mouse button (handled separately for ripple effect)
      if (e.button === 2) return;
      
      // Try to get coordinates, but still delegate even if outside canvas
      // This allows push-to-talk to work even if mouse is released outside
      const coords = getCanvasCoordinates(e.clientX, e.clientY);
      
      // Delegate to all active views (even if outside canvas, for push-to-talk)
      const activeViews = viewManager.getActiveViews();
      for (const view of activeViews) {
        const baseViewInstance = (view.data as any)?._baseViewInstance;
        if (baseViewInstance && typeof baseViewInstance.handleMouseUp === 'function') {
          // Pass coordinates if available, otherwise pass -1, -1 to indicate outside
          baseViewInstance.handleMouseUp(coords?.x ?? -1, coords?.y ?? -1);
        }
      }
    });

    // Handle mouse leave
    this.stageElement.addEventListener('mouseleave', () => {
      // Delegate to all active views
      const activeViews = viewManager.getActiveViews();
      for (const view of activeViews) {
        const baseViewInstance = (view.data as any)?._baseViewInstance;
        if (baseViewInstance && typeof baseViewInstance.handleMouseLeave === 'function') {
          baseViewInstance.handleMouseLeave();
        }
      }
    });
  }

  /**
   * Set up event handlers
   */
  private setupHandlers(): void {
    // Ripple effect: Mouse movement handler (only on very fast movement or Ctrl held)
    if (this.canvasElement && this.rippleEffectState) {
      let lastRippleTime = 0;
      const rippleThrottle = 50; // Minimum milliseconds between ripples (20 ripples per second max)

      // Track mouse position for speed calculation
      let lastMouseX = 0;
      let lastMouseY = 0;
      let lastMouseTime = 0;
      const fastMovementThreshold = 50; // Pixels per millisecond threshold - only very fast/thrashing movements (increased from 10)

      // Helper function to convert mouse position to canvas coordinates
      const getCanvasCoordinates = (clientX: number, clientY: number): { x: number; y: number } | null => {
        if (!this.canvasElement || !state.grid) return null;

        const rect = this.canvasElement.getBoundingClientRect();

        // Get actual canvas drawing buffer dimensions
        const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
        const canvasWidth = this.canvasElement.width || state.grid.width * dpr;
        const canvasHeight = this.canvasElement.height || state.grid.height * dpr;

        // Get mouse position in canvas coordinates
        // WebGL uses bottom-left origin, so flip Y
        const x = ((clientX - rect.left) / rect.width) * canvasWidth;
        const y = canvasHeight - ((clientY - rect.top) / rect.height) * canvasHeight;

        return { x, y };
      };

      // Helper function to add ripple at mouse position
      const addRippleAtMousePosition = (clientX: number, clientY: number) => {
        if (!this.rippleEffectState) return;

        const coords = getCanvasCoordinates(clientX, clientY);
        if (!coords) return;

        const now = performance.now();
        // Throttle ripples to avoid too many
        if (now - lastRippleTime < rippleThrottle) return;
        lastRippleTime = now;

        const time = performance.now() * 0.001;
        // User clicks use full intensity (1.0) for visible feedback
        addRipple(this.rippleEffectState, coords.x, coords.y, time, 1.0);

        // Mark ripple canvas for render
        this.rippleEffectState.active = true;
        this.renderer.markDirty();
        this.animationLoop.markRedraw();
        this.animationLoop.start();
      };

      // Mouse movement handler - ripple on fast movement only (removed right-click/Ctrl+Click bulge/ripple)
      this.stageElement?.addEventListener('mousemove', (e) => {
        const now = performance.now();

        // Calculate mouse speed if we have previous position
        let isVeryFastMovement = false;
        if (lastMouseTime > 0) {
          const timeDelta = now - lastMouseTime;
          if (timeDelta > 0) {
            const dx = e.clientX - lastMouseX;
            const dy = e.clientY - lastMouseY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const speed = distance / timeDelta; // pixels per millisecond
            isVeryFastMovement = speed > fastMovementThreshold; // Only very fast movements
          }
        }

        // Update tracking variables
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;
        lastMouseTime = now;

        // Only add ripple if moving very fast
        if (isVeryFastMovement) {
          addRippleAtMousePosition(e.clientX, e.clientY);
        }
      });
    }

    // Canvas interaction handlers (for view buttons, etc.)
    this.setupCanvasInteractions();
    // The canvas circle is rendered for visual feedback, but clicks/hovers are handled by DOM element
    // This follows the standard UI pattern: canvas for rendering, DOM for interaction

    // Keyboard controls for background cycling and game movement
    document.addEventListener('keydown', (e) => {
      // Game mode: WASD controls (only when in game mode)
      if (this.isGameMode()) {
        const key = e.key.toLowerCase();
        if (key === 'w' || key === 's' || key === 'a' || key === 'd') {
          e.preventDefault();
          this.handleGameMovement(key === 'd', key === 'w', key === 's');
        }
      }

      // Kaleidoscope mode: 'A' key cycles the pattern
      if (this.isKaleidoscopeMode() && e.key.toLowerCase() === 'a') {
        // Only prevent default if not typing in an input/textarea
        const target = e.target as HTMLElement;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
          // Don't prevent default when typing, but still update URL feature
          this.cycleKaleidoscope();
        } else {
          // Prevent default when not typing in input (normal shortcut behavior)
          e.preventDefault();
          this.cycleKaleidoscope();
        }
      }

      // Numpad keys (1-9) for manual mode selection, 0 to turn off override
      const isNumpad = e.key.startsWith('Numpad') || e.location === 3;
      if (isNumpad) {
        e.preventDefault();
        let numpadKey: string;
        if (e.key.startsWith('Numpad')) {
          numpadKey = e.key.replace('Numpad', '');
        } else {
          numpadKey = e.key;
        }

        if (numpadKey === '0') {
          // Turn off override, return to normal cycling
          this.urlManager.setModeOverride(null);
        } else {
          // Map numpad keys to modes
          const modeMap: Record<string, 'clock' | 'ticker' | 'game' | 'kaleidoscope'> = {
            '1': 'clock',
            '2': 'ticker',
            '3': 'game',
            '4': 'kaleidoscope',
            '5': 'clock', // Cycle through modes
            '6': 'ticker',
            '7': 'game',
            '8': 'kaleidoscope',
            '9': 'clock',
          };
          const mode = modeMap[numpadKey];
          if (mode) {
            this.urlManager.setModeOverride(mode);
          }
        }
      }

      // Background cycling always uses left/right arrows
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        this.cycleBackground(e.key === 'ArrowRight');
      }

      // Page pagination: up/down arrows switch between pages
      // Only if not in game mode (game mode uses WASD)
      if (!this.isGameMode() && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        this.switchPage(e.key === 'ArrowDown');
      }
    });
  }

  /**
   * Set up drag-drop handlers
   */
  private setupDragDropHandlers(): void {
    if (!this.stageElement) return;

    // Prevent default drag behavior
    this.stageElement.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Check if dragging over metaball overlay
      import('../services/majaku-privacy-background/metaball-drag-state.js').then(({ checkDragOverMetaball, handleDragOverMetaball, handleDragLeaveMetaball }) => {
        const isOverMetaball = checkDragOverMetaball(e.clientX, e.clientY);
        if (isOverMetaball) {
          handleDragOverMetaball();
        } else {
          handleDragLeaveMetaball();
        }
      }).catch(() => {
        // Metaball drag state not available, ignore
      });
      
      // Check if over drop zone
      const coords = this.getCanvasCoordinates(e.clientX, e.clientY);
      if (coords && state.grid) {
        const wasOverZone = this.dragDropZoneState.isOverZone;
        this.dragDropZoneState.isOverZone = isPointInDragDropZone(coords.x, coords.y, state.grid);
        this.dragDropZoneState.isDragging = true;
        
        if (wasOverZone !== this.dragDropZoneState.isOverZone) {
          this.renderer.markDirty();
          this.animationLoop.markRedraw();
          this.animationLoop.start();
        }
      }
    });

    this.stageElement.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Only clear if leaving the stage element entirely
      if (!this.stageElement?.contains(e.relatedTarget as Node)) {
        this.dragDropZoneState.isDragging = false;
        this.dragDropZoneState.isOverZone = false;
        this.renderer.markDirty();
        this.animationLoop.markRedraw();
        this.animationLoop.start();
      }
    });

    this.stageElement.addEventListener('drop', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      this.dragDropZoneState.isDragging = false;
      this.dragDropZoneState.isOverZone = false;
      
      // Check if dropped over metaball
      const { checkDragOverMetaball, handleDragEnd } = await import('../services/majaku-privacy-background/metaball-drag-state.js').catch(() => ({ checkDragOverMetaball: () => false, handleDragEnd: () => {} }));
      const isOverMetaball = checkDragOverMetaball(e.clientX, e.clientY);
      
      const coords = this.getCanvasCoordinates(e.clientX, e.clientY);
      if (coords && state.grid && isPointInDragDropZone(coords.x, coords.y, state.grid)) {
        // Handle file drops
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          const { dragDropContext } = await import('../systems/drag-drop-context.js');
          for (let i = 0; i < e.dataTransfer.files.length; i++) {
            dragDropContext.addFile(e.dataTransfer.files[i]);
          }
        }
        
        // Handle card drags (from dataTransfer)
        const cardData = e.dataTransfer?.getData('application/x-pivot-card');
        if (cardData) {
          try {
            const card = JSON.parse(cardData);
            const { dragDropContext } = await import('../systems/drag-drop-context.js');
            dragDropContext.addCard(card);
          } catch (error) {
            // Ignore parse errors
          }
        }
      }
      
      // Handle metaball drag end (will transition to loading, then check for uploads)
      if (isOverMetaball) {
        handleDragEnd();
      }
      
      this.renderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    });

    // Track drag start
    document.addEventListener('dragstart', () => {
      this.dragDropZoneState.isDragging = true;
      this.renderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    });

    // Track drag end
    document.addEventListener('dragend', () => {
      this.dragDropZoneState.isDragging = false;
      this.dragDropZoneState.isOverZone = false;
      
      // Handle metaball drag end
      import('../services/majaku-privacy-background/metaball-drag-state.js').then(({ handleDragEnd }) => {
        handleDragEnd();
      }).catch(() => {
        // Metaball drag state not available, ignore
      });
      
      this.renderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    });
  }

  /**
   * Get canvas coordinates from client coordinates
   */
  private getCanvasCoordinates(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.canvasElement || !state.grid) return null;
    const rect = this.canvasElement.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * state.grid.width;
    const y = ((clientY - rect.top) / rect.height) * state.grid.height;
    return { x, y };
  }

  /**
   * Set up resize handler
   */
  private setupResizeHandler(): void {
    // CRITICAL: Single resize coordinator - prevents duplicate handlers
    // All resize events handled here to prevent event cascade
    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Recompute grid - Processes/Views handle their own layout
        // recompute() calls viewportAllocationIntegration.updateGridState()
        // which triggers allocation and syncs state.elements
        if (state.grid) {
          this.recompute();
        }
      }, 50);
    });
  }

  /**
   * Apply layout from current state
   * 
   * Processes/Views handle their own layout via Viewport Allocation Manager.
   */
  private async applyLayoutFromState(): Promise<void> {
    // Processes/Views handle layout via Viewport Allocation Manager
    if (state.grid) {
      // Just recompute - Processes/Views will allocate themselves
      this.recompute();
    }
  }

  // Layout is handled by Processes/Views via Viewport Allocation Manager

  /**
   * Recompute grid and allocate elements
   * Also marks both canvases as dirty when grid size changes
   * 
   * Uses the two-layer allocation system for processes/views.
   */
  private recompute(): void {
    if (!state.grid || !state.elements) return;

    // Prevent recursive calls
    if (this.isRecomputing) {
      return;
    }

    this.isRecomputing = true;

    try {
      const traceId = trace.start('recompute-grid');

      // Update grid using Immer (no events emitted yet - we'll emit after allocation)
      const gridUpdates = computeGridResolution(
        state.grid,
        this.stageElement,
        this.logoImageState.image,
        this.logoImageState.loaded
      );
      setState('grid', { ...state.grid!, ...gridUpdates });

      // CRITICAL: Update viewport allocation integration with new grid state
      // This ensures the integration reallocates views with new grid dimensions
      // updateGridState() will call allocate() and sync state.elements
      if (state.grid) {
        viewportAllocationIntegration.updateGridState(state.grid);
        // updateGridState() already handles allocation and state.elements sync
      } else {
        // Fallback: clear elements if no grid
        setState('elements', []);
        bus.emit('state.elements', []);
      }

      // REMOVED: ensureDom(), updateDOM() - BaseView instances handle their own DOM management
      this.render();
      this.animationLoop.markRedraw();
      this.animationLoop.start();

      trace.end(traceId);
    } finally {
      this.isRecomputing = false;
    }
  }

  /**
   * Ensure DOM elements exist
   */
  // REMOVED: ensureDom() - BaseView instances handle their own DOM creation and updates

  // URL mode methods delegated to UrlManager
  private getUrlMode(): 'clock' | 'ticker' | 'game' | 'kaleidoscope' | 'speech' {
    return this.urlManager.getMode();
  }

  private isTickerMode(): boolean {
    return this.urlManager.isTickerMode();
  }

  private isGameMode(): boolean {
    return this.urlManager.isGameMode();
  }

  private isKaleidoscopeMode(): boolean {
    return this.urlManager.isKaleidoscopeMode();
  }


  // URL ticker methods removed - handled by UrlManager

  /**
   * Handle game movement (WASD keys)
   * W = up, S = down, A = left, D = right
   */
  private handleGameMovement(forward: boolean, up: boolean, down: boolean): void {
    this.urlManager.handleGameMovement(forward, up, down);
  }

  /**
   * Cycle kaleidoscope pattern (shift left half and mirror)
   */
  private cycleKaleidoscope(): void {
    this.urlManager.cycleKaleidoscope();
  }

  /**
   * Switch between pages (pagination)
   * @param forward - true to go to next page, false to go to previous page
   */
  private switchPage(forward: boolean): void {
    // MVP: Single page only, pagination disabled
    // TODO: Re-implement pagination if needed
    return;

    // Trigger re-render
    this.renderer.markDirty();
    this.animationLoop.markRedraw();
    this.animationLoop.start();
  }

  // URL update methods removed - handled by UrlManager

  /**
   * Animation tick
   */
  private tick(): void {
    // Update cursor radius lerp (only when target changes or animating)
    let cursorIsAnimating = false;
    if (this.cursorElement && this.cursorElement.style.display !== 'none') {
      // Only animate if target changed or already animating
      const targetChanged = this.cursorTargetRadius !== this.cursorLastTargetRadius;
      if (targetChanged) {
        this.cursorLastTargetRadius = this.cursorTargetRadius;
        this.cursorAnimationActive = true;
      }
      
      if (this.cursorAnimationActive) {
        const radiusDiff = this.cursorTargetRadius - this.cursorCurrentRadius;
        this.cursorCurrentRadius += radiusDiff * this.cursorLerpSpeed;
        
        // Check if cursor is still animating (not yet at target) - check after update
        const remainingDiff = this.cursorTargetRadius - this.cursorCurrentRadius;
        cursorIsAnimating = Math.abs(remainingDiff) > 0.01;
        
        // Stop animation if reached target
        if (!cursorIsAnimating) {
          this.cursorAnimationActive = false;
          this.cursorCurrentRadius = this.cursorTargetRadius; // Snap to exact value
        }
        
        // Update cursor size
        const size = Math.max(0, this.cursorCurrentRadius * 2);
        this.cursorElement.style.width = `${size}px`;
        this.cursorElement.style.height = `${size}px`;
      }
      
      // Always use hollow circle (both idle and active states)
      // Active state uses thicker border, idle state uses thinner border
      if (this.isHoveringClickable) {
        // Active state - thicker border for visibility
        this.cursorElement.style.border = `${this.cursorBorderWidthActive}px solid white`;
        this.cursorElement.style.backgroundColor = 'transparent';
      } else {
        // Idle state - thinner border
        this.cursorElement.style.border = `${this.cursorBorderWidthIdle}px solid white`;
        this.cursorElement.style.backgroundColor = 'transparent';
      }
    }

    if (!state.elements || !state.grid) {
      return;
    }

    // Update FPS in state (throttled - only update when FPS changes significantly)
    const currentFPS = this.animationLoop.getFPS();
    const fpsDiff = state.ui?.fps ? Math.abs(currentFPS - state.ui.fps) : currentFPS;
    if (fpsDiff >= 5) { // Only update if FPS changed by 5 or more
      updateState('ui', (draft) => {
        if (!draft) draft = { selection: null, fps: 0 };
        draft.fps = currentFPS;
      });
    }

    // Update URL clock, ticker, game, or splash screen
    // Update splash screen every frame for smooth animation
    this.urlManager.update();

    // Check if in workshop mode
    if (this.urlManager.isWorkshopMode()) {
      import('../services/majaku-privacy-background/index.js').then(({ setWorkshopMode }) => {
        setWorkshopMode(true);
      });
      import('./workshop/workshop-overlay.js').then(({ initializeWorkshopOverlay }) => {
        initializeWorkshopOverlay();
      });
      // Skip other updates in workshop mode
      return;
    }

    // Update view animations (runs every frame)
    const deltaTime = 16.67; // ~60fps delta time
    viewAnimationManager.update(deltaTime);
    
    // Mark renderer dirty if animations are active
    if (viewAnimationManager.isActive()) {
      this.renderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    }

    // Animation removed - elements don't have animation state
    // Elements are positioned directly by the allocator

    // Update logo animation
    if (this.logoImageState.loaded) {
      const wasRotating = this.logoAnimState.isRotating;
      updateLogoState(this.logoAnimState, performance.now());
      // Mark dirty when rotating starts or during rotation
      if (this.logoAnimState.isRotating || wasRotating) {
        this.renderer.markDirty();
      }
    }

    // Update LED flash animation
    const now = performance.now();
    updateLedFlashState(this.ledFlashState, now);
    if (this.ledFlashState.flashIntensity > 0) {
      this.renderer.markDirty();
    }


    // If video background is active, mark background canvas dirty for continuous rendering
    // and keep animation loop active to render video frames
    if (this.bgState.type === 'video' && this.bgState.video?.loaded) {
      this.backgroundRenderer.markDirty();
      this.animationLoop.markRedraw();
      this.animationLoop.start();
    }

    // Mark background canvas dirty if background image loads or if using gradient/checkerboard
    if (this.bgState.type === 'image' && this.bgState.image?.loaded) {
      this.backgroundRenderer.markDirty();
    } else if (this.bgState.type === 'gradient' || this.bgState.type === 'checkerboard') {
      // Gradient/checkerboard backgrounds always need to be rendered
      this.backgroundRenderer.markDirty();
    }

    // Ripple effects are handled by WebGL shader system (rippleEffectState)
    // Animation loop is kept running by rippleEffectState.active flag

    // REMOVED: updateDOM() - BaseView instances handle their own DOM updates

    // PERFORMANCE: Only render when actually needed
    const bgReady = (this.bgState.type === 'image' && this.bgState.image?.loaded) ||
      (this.bgState.type === 'gradient') ||
      (this.bgState.type === 'checkerboard') ||
      (this.bgState.type === 'video' && this.bgState.video?.loaded) ||
      (this.bgState.type === 'drawing'); // Always render for drawing mode
    
    // Only render if something actually changed or is animating
    const hasActiveAnimations = this.logoAnimState.isRotating || 
      this.ledFlashState.flashIntensity > 0 ||
      (this.bgState.type === 'video' && this.bgState.video?.loaded) ||
      (this.rippleEffectState?.active === true) ||
      cursorIsAnimating; // Keep loop running while cursor is animating
    
    // Check if we actually need to render
    const needsRender = this.animationLoop.getNeedsRedraw() && (bgReady || this.logoImageState.loaded);
    
    if (needsRender || hasActiveAnimations) {
      this.render();
      this.animationLoop.clearRedraw();
    }
    
    // Keep animation loop running if cursor is still animating
    // Mark redraw to prevent loop from stopping
    if (cursorIsAnimating) {
      this.animationLoop.markRedraw();
    }
    
    // Stop animation loop if nothing is animating and nothing is dirty
    // (The loop will auto-stop in its tick method if needsRedraw is false)
  }

  /**
   * Cycle between available backgrounds
   */
  private cycleBackground(forward: boolean): void {
    if (forward) {
      this.currentBackgroundIndex = (this.currentBackgroundIndex + 1) % this.backgrounds.length;
    } else {
      this.currentBackgroundIndex = (this.currentBackgroundIndex - 1 + this.backgrounds.length) % this.backgrounds.length;
    }

    const selectedBg = this.backgrounds[this.currentBackgroundIndex];
    this.bgState = {
      type: selectedBg.type,
      image: selectedBg.image,
      gradient: selectedBg.gradient,
      checkerboard: selectedBg.checkerboard,
      video: selectedBg.video,
    };

    // Update state with background type and data to avoid brittle index checking
    updateState('ui', (draft) => {
      if (!draft) draft = {};
      draft.backgroundIndex = this.currentBackgroundIndex;
      draft.backgroundType = selectedBg.type;
      draft.backgroundData = selectedBg.gradient ? { gradient: selectedBg.gradient } : undefined;
    });
    
    // Update theme based on new background
    this.currentTheme = getCurrentTheme(this.currentBackgroundIndex, this.backgrounds);
    this.applyTheme(this.currentTheme);

    // Handle majaku-privacy background activation/deactivation
    if (selectedBg.type === 'majaku-privacy') {
      activateMajakuPrivacyBackground();
      // Hide the regular background canvas when majaku-privacy is active
      if (this.backgroundCanvasElement) {
        this.backgroundCanvasElement.style.display = 'none';
      }
    } else {
      deactivateMajakuPrivacyBackground();
      // Show the regular background canvas for other backgrounds
      if (this.backgroundCanvasElement) {
        this.backgroundCanvasElement.style.display = 'block';
      }
    }

    // Handle drawing background activation/deactivation
    // Emit events for drawing process to handle
    if (selectedBg.type === 'drawing') {
      bus.emit('drawing.activate', {});
      // Set pointer-events: none on FX canvas to allow pass-through to background canvas
      if (this.canvasElement) {
        this.canvasElement.style.pointerEvents = 'none';
      }
    } else if (this.bgState?.type === 'drawing') {
      // Deactivate drawing mode when switching away from drawing
      bus.emit('drawing.deactivate', {});
      // Restore pointer-events on FX canvas
      if (this.canvasElement) {
        this.canvasElement.style.pointerEvents = 'auto';
      }
    }
    
    // Emit background changed event for drawing process
    bus.emit('background.changed', { type: selectedBg.type });

    // Save background selection to state
    updateState('ui', (draft) => {
      draft.backgroundIndex = this.currentBackgroundIndex;
    });

    // Mark background canvas dirty and trigger render
    this.backgroundRenderer.markDirty();
    this.render();
    this.animationLoop.markRedraw();
    this.animationLoop.start();
  }
  
  /**
   * Apply theme to UI elements (logo, etc.)
   */
  private applyTheme(theme: Theme): void {
    // Update logo image if theme has different logo
    if (this.logoImageState && this.logoImageState.image) {
      // Extract filename from full URL path
      const currentLogoPath = this.logoImageState.image.src.split('/').pop() || '';
      const newLogoPath = theme.logoImage;
      
      // Compare just the filename (handle both relative and absolute paths)
      if (currentLogoPath !== newLogoPath && !this.logoImageState.image.src.endsWith(newLogoPath)) {
        // Logo path changed, create new logo image state
        this.logoImageState = createLogoImage(newLogoPath, () => {
          this.renderer.markDirty();
          this.animationLoop.markRedraw();
          this.animationLoop.start();
        });
        // Mark renderer as dirty to re-render with new logo
        this.renderer.markDirty();
        this.render();
        this.animationLoop.markRedraw();
      }
    } else if (!this.logoImageState) {
      // Logo not initialized yet, create it
      this.logoImageState = createLogoImage(theme.logoImage, () => {
        this.renderer.markDirty();
        this.animationLoop.markRedraw();
        this.animationLoop.start();
      });
    }
    
    // Emit theme change event for other components to react (BaseView, etc.)
    bus.emit('theme.changed', theme);
  }

  /**
   * Get list of compressed video sources from public/videos folder
   * This will be populated after running the compression script
   */
  private getCompressedVideoSources(): string[] {
    // List of compressed video files (created by scripts/compress-videos.sh)
    // These are super-compressed videos (~100KB each) optimized for heavily blurred backgrounds
    const videoFiles = [
      '/videos/47_UEFOQTM1NTZfVjItMDAzMg.mp4', // Garden with dandelions
      '/videos/01.mp4', // Wind Grass
    ];

    // Return all compressed videos
    return videoFiles;
  }

  /**
   * Render both background and main UI canvases
   */
  private render(): void {
    if (!state.grid || !state.elements) {
      return;
    }
    
    // Increment render frame counter for cache tracking
    this._currentRenderFrameId++;
    const renderStart = performance.now();
    // console.log(`[PERF] [APP] render(): started (frame ${this._currentRenderFrameId})`);

    // Render background canvas (separate from UI)
    this.backgroundRenderer.render(
      state.grid,
      this.bgState
    );
    
    // If in drawing mode, re-render strokes after background renderer
    if (this.bgState?.type === 'drawing') {
      renderAllStrokes();
    }

    // Pagination removed - all views visible in horizontal scroll
    const visibleElements = state.elements || [];

    // No canvas views - all views are HTML-based now
    const getViewCanvasesCallback = () => null;

    // Always render 2D content to offscreen canvas first
    // Pass getViewForElement so dismiss buttons can be rendered
    const offscreenCanvas = this.renderer.render(
      state.grid,
      visibleElements,
      this.logoImageState,
      this.logoAnimState,
      this.ledFlashState,
      'none', // Grid visualization disabled - no longer using canvas-based grid
      true, // renderToOffscreen
      undefined, // DEPRECATED: getViewForElement - not used anymore, views render their own backgrounds
      getViewCanvasesCallback,
      undefined, // Page switcher removed - app now uses single horizontal feed
      undefined, // Backstage controls removed - app now uses single horizontal feed
      this.dragDropZoneState, // Pass drag-drop zone state for rendering
      this.currentTheme ?? undefined, // Pass current theme for text colors and other rendering properties
      this.backgroundCanvasElement // Pass background canvas for frosted glass effect
    );

    // Always render via WebGL on FX canvas for consistent rendering path
    if (this.rippleEffectState && this.canvasElement) {
      // PERFORMANCE: Use cached DPR instead of recalculating every frame
      const dpr = this.renderer.getDPR();
      const width = Math.floor(state.grid.width * dpr);
      const height = Math.floor(state.grid.height * dpr);

      if (this.canvasElement.width !== width || this.canvasElement.height !== height) {
        this.canvasElement.width = width;
        this.canvasElement.height = height;
        this.canvasElement.style.width = state.grid.width + 'px';
        this.canvasElement.style.height = state.grid.height + 'px';
      }

      const webglStart = performance.now();
      const now = performance.now() * 0.001;
      // Copy 2D content to texture
      updateSourceTexture(this.rippleEffectState, offscreenCanvas);
      // Render via WebGL (pass-through or ripple shader)
      renderRipple(this.rippleEffectState, width, height, now);
      const webglDuration = performance.now() - webglStart;
      if (webglDuration > 5) {
        // console.log(`[PERF] [APP] WebGL render: slow - ${webglDuration.toFixed(2)}ms`);
      }
    }
    
    const renderDuration = performance.now() - renderStart;
    if (renderDuration > 16) {
      // console.log(`[PERF] [APP] render(): slow - ${renderDuration.toFixed(2)}ms (frame ${this._currentRenderFrameId})`);
    }
  }
  
  /**
   * Setup horizontal scroll container for views
   */
  private setupHorizontalScrollContainer(): void {
    if (!this.layerElement) return;
    
    // Create content wrapper if it doesn't exist
    let contentWrapper = this.layerElement.querySelector('.layer-content') as HTMLElement;
    if (!contentWrapper) {
      contentWrapper = document.createElement('div');
      contentWrapper.className = 'layer-content';
      this.layerElement.appendChild(contentWrapper);
    }
    
    // Add mouse drag to scroll functionality
    this.setupDragToScroll();
  }
  
  private setupDragToScroll(): void {
    if (!this.layerElement) return;
    
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    
    const handleMouseDown = (e: MouseEvent) => {
      // Only handle left mouse button
      if (e.button !== 0) return;
      
      // Don't drag if clicking on a view or interactive element
      const target = e.target as HTMLElement;
      if (target.closest('.pivot-view') || target.closest('#majaku-metaball-overlay-canvas')) {
        return;
      }
      
      isDragging = true;
      startX = e.pageX - (this.layerElement?.offsetLeft || 0);
      scrollLeft = this.layerElement?.scrollLeft || 0;
      this.layerElement?.style.setProperty('cursor', 'grabbing');
      
      // Play scroll start sound and start continuous scroll
      
      e.preventDefault();
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !this.layerElement) return;
      e.preventDefault();
      const x = e.pageX - (this.layerElement.offsetLeft || 0);
      const walk = (x - startX) * 2; // Scroll speed multiplier
      this.layerElement.scrollLeft = scrollLeft - walk;
      
    };
    
    const handleMouseUp = () => {
      isDragging = false;
      this.layerElement?.style.setProperty('cursor', '');
      
    };
    
    const handleMouseLeave = () => {
      isDragging = false;
      this.layerElement?.style.setProperty('cursor', '');
    };
    
    this.layerElement.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    this.layerElement.addEventListener('mouseleave', handleMouseLeave);
  }
  
  // AI visualization removed - metaball overlay handles this functionality now
}

/**
 * Initialize the application
 */
export function initApp(): void {
  try {
    new App();
  } catch (error) {
  }
}
